import { LitElement, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import type {
  ComponentItem,
  ComponentChip,
  ComponentInsertEvent,
  ComponentDragStartEvent,
  GroupExpandEvent,
} from './types.js';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Categories that render small icon-grid cards (no thumbnail, no preview). */
const ICON_GRID_CATEGORIES = new Set(['Elements', 'Logic']);

/** Preview panel width in px */
const PREVIEW_WIDTH = 360;
/** Preview panel height in px */
const PREVIEW_HEIGHT = 420;
/** Auto-scroll speed: px per animation frame */
const SCROLL_SPEED = 0.6;
/** ms after mouseleave before the preview is hidden */
const PREVIEW_HIDE_DELAY = 180;

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * rd-component-browser
 *
 * A pure display component — it renders what it is given.
 * Data is supplied externally via the `items` property.
 *
 * Emitted events:
 *   component-insert      { component: ComponentItem }
 *   component-drag-start  { component: ComponentItem }
 *   group-expand          { category, subcategory }
 *   cb-search             { query }
 */
export class RDComponentBrowser extends LitElement {
  // Light DOM — styles are in the global SCSS sheet
  createRenderRoot() {
    return this;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  @property({ type: Array }) items: ComponentItem[] = [];
  @property({ type: Boolean }) loading = false;
  /** The category this browser is showing — used to determine card layout. */
  @property({ type: String }) category = '';
  /** Card size variant: 'xs' | 'sm' | 'md' | 'lg'. Default 'sm'. */
  @property({ type: String }) size: 'xs' | 'sm' | 'md' | 'lg' = 'sm';

  // ── Internal state ──────────────────────────────────────────────────

  @state() private _expandedGroups = new Set<string>();
  @state() private _previewItem: ComponentItem | null = null;
  @state() private _previewStyle = '';

  private _previewHideTimer = 0;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupPreview();
  }

  // ── Derived data ───────────────────────────────────────────────────────────

  private get _subcategories(): string[] {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const item of this.items) {
      const sub = item.subcategory ?? '';
      if (!seen.has(sub)) { seen.add(sub); order.push(sub); }
    }
    return order;
  }

  private _itemsInGroup(sub: string): ComponentItem[] {
    return this.items.filter((i) => (i.subcategory ?? '') === sub);
  }

  private _groupKey(sub: string): string {
    return sub || '__root__';
  }

  private _isExpanded(sub: string): boolean {
    return this._expandedGroups.has(this._groupKey(sub));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private _useIconGrid(): boolean {
    return ICON_GRID_CATEGORIES.has(this.category);
  }

  // ── Interactions ───────────────────────────────────────────────────────────

  private _toggleGroup(sub: string) {
    const key = this._groupKey(sub);
    const next = new Set(this._expandedGroups);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      this._emit<GroupExpandEvent>('group-expand', {
        category: this.category,
        subcategory: sub || null,
      });
    }
    this._expandedGroups = next;
  }

  private _handleInsert(item: ComponentItem) {
    if (item.disabled) return;
    this._emit<ComponentInsertEvent>('component-insert', { component: item });
  }

  private _handleDragStart(e: DragEvent, item: ComponentItem) {
    if (item.disabled) { e.preventDefault(); return; }
    this._cleanupPreview();   // hide preview immediately on drag
    e.dataTransfer!.effectAllowed = 'copy';
    e.dataTransfer!.setData('application/rd-component', JSON.stringify(item));

    const ghost = document.createElement('div');
    ghost.className = 'rd-cb-drag-ghost';
    ghost.innerHTML = `${item.icon}<span>${item.name}</span>`;
    Object.assign(ghost.style, { position: 'fixed', top: '-200px', left: '-200px', pointerEvents: 'none', zIndex: '99999' });
    document.body.appendChild(ghost);
    e.dataTransfer!.setDragImage(ghost, 20, 16);
    setTimeout(() => ghost.remove(), 0);

    this._emit<ComponentDragStartEvent>('component-drag-start', { component: item });

    // dragend fires for every drag end — successful drop OR cancel (Escape / outside).
    // Pass the native dropEffect so the canvas can distinguish the two cases.
    (e.currentTarget as HTMLElement).addEventListener('dragend', (de: DragEvent) => {
      this.dispatchEvent(new CustomEvent('component-drag-end', {
        bubbles: true,
        composed: true,
        detail: { component: item, dropEffect: de.dataTransfer?.dropEffect ?? 'none' },
      }));
    }, { once: true });
  }

  // ── Preview ────────────────────────────────────────────────────────────────

  private _showPreview(e: MouseEvent, item: ComponentItem) {
    if (!item.html || ICON_GRID_CATEGORIES.has(item.category)) return;
    clearTimeout(this._previewHideTimer);
    const hostRect = this.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceRight = window.innerWidth - hostRect.right;
    const left = spaceRight >= PREVIEW_WIDTH + 16 ? hostRect.right + 8 : hostRect.left - PREVIEW_WIDTH - 8;
    const cardRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const top = Math.max(8, Math.min(cardRect.top, viewportH - PREVIEW_HEIGHT - 8));
    this._previewStyle = `top:${top}px;left:${left}px;width:${PREVIEW_WIDTH}px;height:${PREVIEW_HEIGHT}px;`;
    this._previewItem = item;
  }

  private _hidePreview() { this._previewHideTimer = window.setTimeout(() => this._cleanupPreview(), PREVIEW_HIDE_DELAY); }
  private _keepPreview()  { clearTimeout(this._previewHideTimer); }
  private _cleanupPreview() { this._previewItem = null; }

  // ── Emit helper ────────────────────────────────────────────────────────────

  private _emit<T>(name: string, detail: T) {
    this.dispatchEvent(new CustomEvent<T>(name, { bubbles: true, composed: true, detail }));
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  render() {
    return html`
      <div class="rd-cb rd-cb--${this.size}">
        <div class="rd-cb__body">
          ${this.loading ? this._renderLoader() : this._renderContent()}
        </div>
      </div>
      ${this._previewItem ? this._renderPreview() : nothing}
    `;
  }

  // ── Content area ───────────────────────────────────────────────────────────

  private _renderContent() {
    const groups = this._subcategories;
    if (groups.length === 0) {
      return html`<div class="rd-cb__empty">No components found</div>`;
    }

    return html`
      <div class="rd-cb__groups">
        ${repeat(groups, (sub) => this._groupKey(sub), (sub) => this._renderGroup(sub))}
      </div>
    `;
  }

  // ── Group ──────────────────────────────────────────────────────────────────

  private _renderGroup(sub: string) {
    const items = this._itemsInGroup(sub);
    const expanded = this._isExpanded(sub);
    const label = sub || this.category || 'Items';

    return html`
      <div class=${classMap({ 'rd-cb__group': true, 'rd-cb__group--expanded': expanded })}>
        <button
          class="rd-cb__group-header"
          @click=${() => this._toggleGroup(sub)}
          aria-expanded=${expanded}
        >
          <i class=${`rd-cb__group-chevron bx bx-chevron-right`}></i>
          <span class="rd-cb__group-title">${label}</span>
          <span class="rd-cb__group-count">${items.length}</span>
        </button>
        ${expanded
          ? html`<div class="rd-cb__group-body">${this._renderCards(items)}</div>`
          : nothing}
      </div>
    `;
  }

  // ── Cards dispatcher ───────────────────────────────────────────────────────

  private _renderCards(items: ComponentItem[]) {
    const useGrid = this._useIconGrid();
    return useGrid
      ? html`<div class="rd-cb__card-grid rd-cb__card-grid--icon">${repeat(items, (i) => i.id, (i) => this._renderIconCard(i))}</div>`
      : html`<div class="rd-cb__card-grid rd-cb__card-grid--thumb">${repeat(items, (i) => i.id, (i) => this._renderThumbCard(i))}</div>`;
  }

  // ── Icon card (Elements / Logic) ───────────────────────────────────────────

  private _renderIconCard(item: ComponentItem) {
    return html`
      <div
        class=${classMap({
          'rd-cb-card': true,
          'rd-cb-card--icon': true,
          [`rd-cb-card--${item.size}`]: !!item.size,
          'rd-cb-card--disabled': !!item.disabled,
          'rd-cb-card--grayout':  !!item.grayout,
          'rd-cb-card--nonvisual': item.type === 'nonvisual',
        })}
        draggable=${!item.disabled}
        title=${item.description || item.name}
        @click=${() => this._handleInsert(item)}
        @dragstart=${(e: DragEvent) => this._handleDragStart(e, item)}
      >
        <div class="rd-cb-card__icon">${unsafeHTML(item.icon)}</div>
        <span class="rd-cb-card__name">${item.name}</span>
        ${item.chips?.length ? this._renderChips(item.chips) : nothing}
      </div>
    `;
  }

  // ── Thumbnail card (Blocks / Pages) ───────────────────────────────────────

  private _renderThumbCard(item: ComponentItem) {
    return html`
      <div
        class=${classMap({
          'rd-cb-card': true,
          'rd-cb-card--thumb': true,
          [`rd-cb-card--${item.size}`]: !!item.size,
          'rd-cb-card--disabled': !!item.disabled,
          'rd-cb-card--grayout':  !!item.grayout,
        })}
        draggable=${!item.disabled}
        @click=${() => this._handleInsert(item)}
        @dragstart=${(e: DragEvent) => this._handleDragStart(e, item)}
        @mouseenter=${(e: MouseEvent) => this._showPreview(e, item)}
        @mouseleave=${() => this._hidePreview()}
      >
        <div class="rd-cb-card__thumb">
          ${item.thumbnail
            ? html`<img src=${item.thumbnail} alt=${item.name} loading="lazy" />`
            : html`<div class="rd-cb-card__thumb-fallback">${unsafeHTML(item.icon)}</div>`}
        </div>
        <div class="rd-cb-card__foot">
          <span class="rd-cb-card__name">${item.name}</span>
        </div>
        ${item.chips?.length ? this._renderChips(item.chips) : nothing}
      </div>
    `;
  }

  // ── Chips ─────────────────────────────────────────────────────

  private _renderChips(chips: ComponentChip[]) {
    // Group chips by position so stacked chips share one container
    const groups = new Map<string, ComponentChip[]>();
    for (const chip of chips) {
      const pos = chip.position ?? 'topright';
      if (!groups.has(pos)) groups.set(pos, []);
      groups.get(pos)!.push(chip);
    }
    return html`${[...groups.entries()].map(([pos, group]) => html`
      <div class="rd-cb-card__chips rd-cb-card__chips--${pos}">
        ${group.map(c => html`<span class="rd-cb-chip">${unsafeHTML(c.html)}</span>`)}
      </div>
    `)}`;
  }

  // ── Loader ─────────────────────────────────────────────────────────────────

  private _renderLoader() {
    return html`
      <div class="rd-cb__loader">
        <div class="rd-cb__spinner"></div>
        <span>Loading…</span>
      </div>
    `;
  }

  // ── Floating preview panel ────────────────────────────────────────────────

  private _renderPreview() {
    const item = this._previewItem!;
    return html`
      <div
        class="rd-cb-preview"
        style=${this._previewStyle}
        @mouseenter=${this._keepPreview}
        @mouseleave=${this._hidePreview}
      >
        <div class="rd-cb-preview__header">
          <span class="rd-cb-preview__icon">${unsafeHTML(item.icon)}</span>
          <span class="rd-cb-preview__name">${item.name}</span>
          <span class="rd-cb-preview__tag">${item.subcategory || item.category}</span>
        </div>
        <div class="rd-cb-preview__img-wrap">
          ${item.thumbnail
            ? html`<img class="rd-cb-preview__img" src=${item.thumbnail} alt=${item.name} />`
            : html`<div class="rd-cb-preview__img-fallback">${unsafeHTML(item.icon)}</div>`}
        </div>
        <div class="rd-cb-preview__body">
          ${item.description
            ? html`<p class="rd-cb-preview__desc">${item.description}</p>`
            : nothing}
          ${item.tags?.length
            ? html`<div class="rd-cb-preview__tags">${item.tags.slice(0, 5).map(t =>
                html`<span class="rd-cb-preview__tag-pill">${t}</span>`
              )}</div>`
            : nothing}
          <span class="rd-cb-preview__hint"><i class="bx bx-mouse-alt"></i> drag or click to insert</span>
        </div>
      </div>
    `;
  }
}

customElements.define('rd-component-browser', RDComponentBrowser);
